<template>
  <div class="card">
    <div class="card-header">
      <h5 class="my-2">Product Parts</h5>
    </div>
    <div class="card-body container">
      <div class="row">
        <OfferingAddOnsTable
          class="col"
          title="Features"
          :loading="loading || featureLinkLoading"
          :loading-row-id="featureLoadingRowId"
          :loading-col-key="featureLoadingColKey"
          :items="mappedLinkedFeatures"
          :cols-map="{
            Name: 'name',
            'Visible for Customer': 'customer_visibility',
          }"
          link-table
          @create="openDialog('features')"
          @update="(id: number) => goToUpdateForm('features', id)"
          @delete="(id: number) => removeLink('features', id)"
          @checked="toggleCheck"
        />
        <OfferingAddOnsTable
          class="col"
          title="Service Level Objectives"
          :loading="loading || serviceLevelObjectiveLinkLoading"
          :items="linkedSlos"
          :cols-map="{Name: 'name'}"
          @create="openDialog('serviceLevelObjectives')"
          @update="(id: number) => goToUpdateForm('serviceLevelObjectives', id)"
          @delete="(id: number) => removeLink('serviceLevelObjectives', id)"
        />
      </div>
    </div>
  </div>
  <div class="card">
    <div class="card-header">
      <h5 class="my-2">Internal Components</h5>
    </div>
    <div class="card-body">
      <div class="row">
        <OfferingAddOnsTable
          class="col"
          title="Product Components"
          :loading="loading || productComponentLinkLoading"
          :items="linkedProductComponents"
          :cols-map="{Name: 'name'}"
          @create="openDialog('productComponents')"
          @update="(id: number) => goToUpdateForm('productComponents', id)"
          @delete="(id: number) => removeLink('productComponents', id)"
        />
        <OfferingAddOnsTable
          class="col"
          title="Labor Components"
          :loading="loading || laborComponentLinkLoading"
          :items="linkedLaborComponents"
          :cols-map="{Name: 'name'}"
          @create="openDialog('laborComponents')"
          @update="(id: number) => goToUpdateForm('laborComponents', id)"
          @delete="(id: number) => removeLink('laborComponents', id)"
        />
      </div>
    </div>
  </div>
  <OfferingAddOnsSelectDialog
    :title="activeDialogTitle"
    :items="activeDialogOptions"
    :selected-items="activeSelectedDialogOptions"
    :open="isDialogOpen"
    :loading="isDialogLoading"
    @close="isDialogOpen = false"
    @submit="submitDialog"
  />
</template>
<script lang="ts" setup>
import {MappedFeatureLink} from '@/stores/userManagement/userManagement';

type OfferingAddOns =
  | MappedFeatureLink[]
  | ProductComponent[]
  | LaborComponent[]
  | ServiceLevelObjective[];

const props = defineProps<{
  pid: number;
}>();

defineEmits<{update: [id: number]}>();

const route = useRoute();
const router = useRouter();

const productOfferingsStore = useProductLinksStore();
const featuresStore = useFeaturesStore();
const productComponentsStore = useProductComponentsStore();
const laborComponentsStore = useLaborComponentsStore();
const serviceLevelObjectivesStore = useServiceLevelObjectivesStore();

const getFeatures = computed(() => featuresStore.getFeatures);
const getProductComponents = computed(() => productComponentsStore.getProductComponents);
const getLaborComponents = computed(() => laborComponentsStore.getLaborComponents);
const getServiceLevelObjectives = computed(
  () => serviceLevelObjectivesStore.getServiceLevelObjectives,
);

const loading = ref(true);

// Feature visibility handled at the "link" table level
const linkedFeatures = ref<FeatureLink[]>([]);

const mappedLinkedFeatures: ComputedRef<MappedFeatureLink[]> = computed(
  () =>
    linkedFeatures.value.map((lf: FeatureLink) => {
      const f = getFeatures.value?.find((f) => f.id === lf.product_feature_id);
      return {
        id: lf.product_feature_id,
        name: f!.name,
        customer_visibility: lf.customer_visibility,
      };
    }) || [],
);

const linkedProductComponents = ref<ProductComponent[]>([]);
const linkedLaborComponents = ref<LaborComponent[]>([]);
const linkedSlos = ref<ServiceLevelObjective[]>([]);

const removeItem = (
  list: globalThis.Ref<Feature[] | ProductComponent[] | LaborComponent[] | ServiceLevelObjective[]>,
  id: number,
) => {
  const index = list.value.findIndex((i) => i.id === id);
  if (index >= 0) list.value.splice(index, 1);
};

/**
 * Feature endpoint requires full list of feature links.
 * Separate functions required to update & send linkedFeatures ref
 */
const removeFeatureLink = async (id: number) => {
  try {
    const index = linkedFeatures.value.findIndex((p) => p.product_feature_id === id);
    const updatedFeatures = [...linkedFeatures.value];
    if (index >= 0) updatedFeatures.splice(index, 1); // Request must succeed before slicing state
    await productOfferingsStore.updateFeatureLinks(props.pid, updatedFeatures);
    if (index >= 0) linkedFeatures.value.splice(index, 1);
  } catch (error) {
    console.log(error);
  }
};

const removeLink = async (name: string, id: number) => {
  try {
    switch (name) {
      case 'features':
        await removeFeatureLink(id);
        break;
      case 'productComponents':
        await productOfferingsStore.deleteProductComponentLink(props.pid, id);
        removeItem(linkedProductComponents, id);
        break;
      case 'laborComponents':
        await productOfferingsStore.deleteLaborComponentLink(props.pid, id);
        removeItem(linkedLaborComponents, id);
        break;
      case 'serviceLevelObjectives':
        await productOfferingsStore.deleteServiceLevelObjectiveLink(props.pid, id);
        removeItem(linkedSlos, id);
        break;
    }
  } catch (errorMessage) {
    console.log(errorMessage);
  }
};

const featureLinkLoading = ref(false);
const productComponentLinkLoading = ref(false);
const laborComponentLinkLoading = ref(false);
const serviceLevelObjectiveLinkLoading = ref(false);

const fetchFeatureLinks = async () => {
  featureLinkLoading.value = true;
  try {
    const data = await productOfferingsStore.readFeatureLinks(props.pid);
    linkedFeatures.value = data || [];
  } catch (errorMessage) {
    console.log(errorMessage);
  } finally {
    featureLinkLoading.value = false;
  }
};

const fetchProductComponentLinks = async () => {
  productComponentLinkLoading.value = true;
  try {
    const data = await productOfferingsStore.readProductComponentLinks(props.pid);
    linkedProductComponents.value = data?.items || [];
  } catch (errorMessage) {
    console.log(errorMessage);
  } finally {
    productComponentLinkLoading.value = false;
  }
};

const fetchLaborComponentLinks = async () => {
  laborComponentLinkLoading.value = true;
  try {
    const data = await productOfferingsStore.readLaborComponentLinks(props.pid);
    linkedLaborComponents.value = data?.items || [];
  } catch (errorMessage) {
    console.log(errorMessage);
  } finally {
    laborComponentLinkLoading.value = false;
  }
};

const fetchServiceLevelObjectiveLinks = async () => {
  serviceLevelObjectiveLinkLoading.value = true;
  try {
    const data = await productOfferingsStore.readServiceLevelObjectiveLinks(props.pid);
    linkedSlos.value = data?.items || [];
  } catch (errorMessage) {
    console.log(errorMessage);
  } finally {
    serviceLevelObjectiveLinkLoading.value = false;
  }
};

const fetchLinks = async () => {
  loading.value = true;
  try {
    await Promise.all([
      fetchFeatureLinks(),
      fetchProductComponentLinks(),
      fetchLaborComponentLinks(),
      fetchServiceLevelObjectiveLinks(),
    ]);
  } catch (errorMessage) {
    console.log(errorMessage);
  } finally {
    loading.value = false;
  }
};

const activeDialogType = ref('');
const activeDialogTitle = ref('');

const isDialogOpen = ref(false);
const isDialogLoading = ref(false);
const openDialog = (type: string) => {
  activeDialogType.value = type;
  activeDialogTitle.value =
    type.charAt(0).toUpperCase() +
    type
      .slice(1)
      .match(/[A-Z]?[a-z]+|[0-9]+|[A-Z]+(?![a-z])/g)
      ?.join(' ');
  isDialogOpen.value = true;
};

const activeSelectedDialogOptions = computed((): OfferingAddOns => {
  switch (activeDialogType.value) {
    case 'features':
      return mappedLinkedFeatures.value;
    case 'productComponents':
      return linkedProductComponents.value;
    case 'laborComponents':
      return linkedLaborComponents.value;
    case 'serviceLevelObjectives':
      return linkedSlos.value;
    default:
      return [];
  }
});
const activeDialogOptions = computed(() => {
  switch (activeDialogType.value) {
    case 'features':
      return getFeatures.value || [];
    case 'productComponents':
      return getProductComponents.value || [];
    case 'laborComponents':
      return getLaborComponents.value || [];
    case 'serviceLevelObjectives':
      return getServiceLevelObjectives.value || [];
    default:
      return [];
  }
});

const featureLoadingRowId = ref<number | undefined>();
const featureLoadingColKey = ref<string | undefined>();

/**
 * Updates a customer_visibility in linkedFeatures then dispatches update
 */
const toggleCheck = async (id: number, key: string, value: boolean) => {
  featureLoadingRowId.value = id;
  featureLoadingColKey.value = key;
  try {
    const index = linkedFeatures.value.findIndex((lf) => lf.product_feature_id === id);
    Object.assign(linkedFeatures.value[index], {
      ...linkedFeatures.value[index],
      customer_visibility: value,
    });
    await productOfferingsStore.updateFeatureLinks(props.pid, linkedFeatures.value);
  } catch (error) {
    console.log(error);
  } finally {
    setTimeout(() => {
      featureLoadingRowId.value = undefined;
      featureLoadingColKey.value = undefined;
    }, 1000);
  }
};

const submitDialog = async (items: OfferingAddOns) => {
  isDialogLoading.value = true;
  let promises = undefined;
  let fetchPromise = undefined;

  const getFeaturePromises = (items: OfferingAddOns) => {
    // Maps items in Feature type back to FeatureLink
    const featuresToAdd: FeatureLink[] = items.map((i) => ({
      product_feature_id: i.id,
      customer_visibility: false,
    }));
    return [
      productOfferingsStore.updateFeatureLinks(props.pid, [
        ...featuresToAdd,
        ...linkedFeatures.value,
      ]),
    ];
  };

  switch (activeDialogType.value) {
    case 'features':
      promises = getFeaturePromises(items);
      fetchPromise = fetchFeatureLinks;
      break;
    case 'productComponents':
      promises = items.map((item) =>
        productOfferingsStore.createProductComponentLink(props.pid, item.id),
      );
      fetchPromise = fetchProductComponentLinks;
      break;
    case 'laborComponents':
      promises = items.map((item) =>
        productOfferingsStore.createLaborComponentLink(props.pid, item.id),
      );
      fetchPromise = fetchLaborComponentLinks;
      break;
    case 'serviceLevelObjectives':
      promises = items.map((item) =>
        productOfferingsStore.createServiceLevelObjectiveLink(props.pid, item.id),
      );
      fetchPromise = fetchServiceLevelObjectiveLinks;
      break;
  }
  try {
    if (promises) await Promise.all(promises);
    if (fetchPromise) await fetchPromise();
  } catch (error) {
    console.log(error);
  } finally {
    isDialogLoading.value = false;
    isDialogOpen.value = false;
  }
};

const goToUpdateForm = (name: string, id: number) => {
  let path = undefined;
  switch (name) {
    case 'features':
      path = route.path.replace('/product_overview', '/product_parts/features');
      break;
    case 'productComponents':
      path = route.path.replace('/product_overview', '/internal_components/product_components');
      break;
    case 'laborComponents':
      path = route.path.replace('/product_overview', '/internal_components/labor_components');
      break;
    case 'serviceLevelObjectives':
      path = route.path.replace('/product_overview', '/product_parts/service_level_objectives');
      break;
  }
  if (path !== undefined) {
    path = path.replace(`/${props.pid}`, `/${id}`);
    router.push({path});
  }
};

onMounted(() => fetchLinks());
</script>
